<template>
    <article class="card-ghost-site card-site" :class="type" :id="item.id">
        <NuxtLink class="image-wrapper" :to="`/sites/${item.id}`" @click.native="saveSite">
            <mess-image :src="photo" placeholder loading="lazy" :sizes="imageSizes" />
        </NuxtLink>

        <div class="content">
            <div class="copy">
                <p v-if="type !== 'large' && neighborhoodName" class="gray-tag">{{ neighborhoodName }}</p>
                <NuxtLink :to="`/sites/${item.id}`" @click.native="saveSite">
                    <h3 class="headline-2">{{ item.fields['Site'] }}</h3>
                </NuxtLink>

                <p v-if="type === 'large' && neighborhoodName" class="gray-tag">{{ neighborhoodName }}</p>
                <p v-if="item && item.fields && item.fields['Site Address']" class="address" :class="{ 'body-small': type !== 'map', 'body-book': type === 'map' }">
                    {{ item.fields['Site Address'] }}
                </p>
            </div>

            <div class="footer">
                <GhostUserActionButton list="ohcFavoriteSites" :item="item" :key="item.id" />
                <GhostUserActionButton :isGhost="true" list="ohcItineraries" :item="item" :itinerary="itinerary" :key="item.id" @click.prevent="updateSingleItem(item.id)" />
            </div>
        </div>
    </article>
</template>

<script setup>
    // Stores
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    const neighborhoodsStore = useNeighborhoodsStore();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;

    // Composables
    const { getSiteNeighborhoodName } = useUtils();
    const { getImageFromArray } = useImageUrl();

    // Emits
    const emit = defineEmits(['updateSingleItem']);

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: false,
            default: 'large',
        },
    });
    const { itinerary, item, type } = toRefs(props);

    // Mirrors Cards/site.vue: the slot depends on the card type.
    const imageSizes = computed(() => SIZES_SITE_CARD[type.value] || SIZES_SITE_CARD.large);

    // Variables
    const neighborhoodName = getSiteNeighborhoodName(item, neighborhoodsMap);

    // Computed
    const photo = computed(() => {
        if (item.value?.fields?.['Selected Photos']) {
            return getImageFromArray(item.value.fields['Selected Photos'], 0);
        }
        return '';
    });

    // Methods
    const saveSite = () => {
        if (!window.siteQuery) {
            window.siteQuery = {
                savedSite: item.value.id,
            };
        } else {
            window.siteQuery.savedSite = item.value.id;
        }
    };

    function updateSingleItem(item) {
        emit('updateSingleItem', JSON.stringify(item));
    }
</script>

<style lang="scss" scoped>
    .card-site {
        background-color: $white;

        .image-wrapper {
            display: block;
            position: relative;
            margin-bottom: 0.5em;
        }

        img {
            position: absolute;
            inset: 0 0 0 0;
            height: 100%;
            width: 100%;
            object-fit: cover;
            object-position: center;
        }

        &.large {
            .image-wrapper {
                padding-top: 60%;
                height: 0;
                width: 100%;
            }

            .gray-tag {
                margin: 10px 0;
            }

            .content {
                padding: 10px 0;
                width: 100%;
            }
        }

        &.list,
        &.map {
            display: flex;

            .image-wrapper {
                width: 70px;
                height: 70px;
                flex-shrink: 0;
                margin-right: 17px;
            }

            h3 {
                margin-bottom: 10px;
            }
        }

        &.map {
            box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
            padding: 15px;

            .gray-tag {
                margin: 0 0 12px;
            }

            .footer {
                margin-top: 12px;
            }
        }

        &.list {
            padding: 20px 0;
            border-bottom: 1px solid #9d9fa2;

            .gray-tag {
                margin: 0 0 6px;
            }

            .content {
                display: flex;
                flex-grow: 1;
            }

            .footer {
                flex-grow: 0;
                flex-shrink: 0;
                margin: 0;
                flex-direction: column;

                button {
                    margin: 0;

                    + button {
                        margin-top: 18px;
                    }
                }
            }

            .copy {
                padding-right: 18px;
                flex-grow: 1;
            }
        }

        .footer {
            display: flex;
            margin-top: 10px;

            button {
                margin-right: 15px;
            }
        }

        .headline-3 {
            padding-right: 9%;
        }
    }
</style>
